import type { RailFeature } from './types';

interface GeoJsonFeature {
  type: 'Feature';
  id?: string | number;
  geometry: { type: string; coordinates: unknown };
  properties: Record<string, unknown>;
}

interface GeoJsonFC {
  type: 'FeatureCollection';
  features: GeoJsonFeature[];
}

/**
 * Load rail features from a static GeoJSON file (produced by the offline
 * pipeline/rail-tiling step). Keeping the data static keeps the runtime fully
 * client-side and dodges ArcGIS CORS issues (Risk S1).
 */
export async function loadRailGeoJson(url: string): Promise<RailFeature[]> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`rail load failed: ${res.status} ${url}`);
  const fc = (await res.json()) as GeoJsonFC;
  return fc.features.flatMap((f, index) => toRailFeatures(f, index));
}

function toRailFeatures(f: GeoJsonFeature, index: number): RailFeature[] {
  // ArcGIS publishes lowercase field names (electrified/gauge/operational_status),
  // but other exports may use upper case — look up either casing.
  const prop = (name: string): unknown =>
    f.properties[name] ?? f.properties[name.toUpperCase()] ?? f.properties[name.toLowerCase()];

  const attrs = {
    electrified: /^electrified$/i.test(String(prop('electrified') ?? '')),
    gauge: (prop('gauge') as string) ?? null,
    operationalStatus: (prop('operational_status') as string) ?? null,
  };

  const lines = extractLines(f.geometry);
  // The pipeline output has no per-feature ids, so fall back to the feature's
  // index — ids MUST be unique or the near-LOD manager collapses all features
  // into one midpoint entry and never finds build candidates.
  return lines.map((line, i) => ({
    id: `${f.id ?? `rail-${index}`}-${i}`,
    line,
    attributes: attrs,
  }));
}

function extractLines(geom: GeoJsonFeature['geometry']): Array<Array<[number, number]>> {
  const coords = geom.coordinates as number[][] | number[][][];
  if (geom.type === 'LineString') {
    return [(coords as number[][]).map((c) => [c[0], c[1]] as [number, number])];
  }
  if (geom.type === 'MultiLineString') {
    return (coords as number[][][]).map((line) =>
      line.map((c) => [c[0], c[1]] as [number, number]),
    );
  }
  return [];
}
